import React, { useState, useEffect } from "react";
import { 
  Search, ExternalLink, Bell, BellOff, TrendingUp, 
  Calendar, AlertCircle, ChevronLeft, ChevronRight, 
  Crown, Loader2, X, Tag, Clock, Sparkles
} from "lucide-react";
import { Capacitor } from '@capacitor/core'; // <-- added import
import { useTranslation } from '../../../contexts/LanguageContext';
import { useNavigate } from "react-router-dom";
import MobileBottomNav from "./MobileBottomNav";
import { useAuth } from "../../../App";

const MobileTrackedMerchantPromoList = ({ onToggleNotification }) => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { httpClient, validateToken, user } = useAuth();
  
  // Detect platform
  const isIOS = Capacitor.getPlatform() === 'ios'; // <-- added

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [trackedMerchants, setTrackedMerchants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const [currentTogglingId, setCurrentTogglingId] = useState(null);
  const [trackedCount, setTrackedCount] = useState(0);
  const [isPremium, setIsPremium] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [merchantToToggle, setMerchantToToggle] = useState(null);
  const [currentToggleStatus, setCurrentToggleStatus] = useState(null);
  const [activePromosData, setActivePromosData] = useState({});
  const [loadingPromos, setLoadingPromos] = useState({});
  const [currentTime, setCurrentTime] = useState(new Date());
  
  const itemsPerPage = 5;

  // Update current time every second for countdown timers
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch tracked merchants, count, and premium status
  useEffect(() => {
    fetchTrackedData();
  }, []);

  useEffect(() => {
    if (trackedMerchants.length > 0) {
      fetchAllMerchantPromos();
    }
  }, [trackedMerchants]);

  const fetchTrackedData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (!httpClient) {
        console.error("httpClient not available");
        setIsLoading(false);
        return;
      }

      await fetchPremiumStatus();
      await Promise.all([
        fetchTrackedMerchants(),
        fetchTrackedCount()
      ]);
    } catch (error) {
      console.error('Error fetching tracked data:', error);
      setError(t('trackedMerchants.errorLoading'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPremiumStatus = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/user/premium-status');
      
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          return fetchPremiumStatus();
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setIsPremium(data.isPremium);
    } catch (error) {
      console.error('Error fetching premium status:', error);
      setIsPremium(false);
    }
  };

  const fetchTrackedMerchants = async () => {
    try {
      if (!httpClient) return;
      
      console.log('Fetching tracked merchants via JWT');
      
      const response = await httpClient.request('/api/user-tracked-merchants');
  
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          return fetchTrackedMerchants();
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Tracked merchants received:', data);
      setTrackedMerchants(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching tracked merchants:', error);
      setTrackedMerchants([]);
    }
  };

  const fetchAllMerchantPromos = async () => {
    try {
      const promosData = {};
      const loadingStates = {};
      
      trackedMerchants.forEach(merchant => {
        if (merchant.id) {
          loadingStates[merchant.id] = true;
        }
      });
      setLoadingPromos(loadingStates);

      const promoPromises = trackedMerchants.map(async (merchant) => {
        if (!merchant.id || !httpClient) return null;
        
        try {
          const response = await httpClient.request(`/api/merchant-promos?merchant_id=${merchant.id}`);
          
          if (!response.ok) {
            if (response.status === 401) {
              const revalidated = await validateToken();
              if (!revalidated) {
                navigate("/demo");
                return null;
              }
              return { merchantId: merchant.id, promos: [] };
            }
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          const data = await response.json();
          return { merchantId: merchant.id, promos: data };
        } catch (error) {
          console.error(`Error fetching promos for merchant ${merchant.id}:`, error);
          return { merchantId: merchant.id, promos: [] };
        }
      });

      const results = await Promise.all(promoPromises);
      
      results.forEach(result => {
        if (result) {
          promosData[result.merchantId] = result.promos;
        }
      });

      setActivePromosData(promosData);
      
      const clearedLoadingStates = {};
      Object.keys(loadingStates).forEach(key => {
        clearedLoadingStates[key] = false;
      });
      setLoadingPromos(clearedLoadingStates);
    } catch (error) {
      console.error('Error fetching all merchant promos:', error);
    }
  };

  const fetchTrackedCount = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/user/tracked-merchants/count');

      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          return fetchTrackedCount();
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setTrackedCount(data.count || 0);
    } catch (error) {
      console.error('Error fetching tracked count:', error);
      setTrackedCount(0);
    }
  };

  // Calculate tracking limit
  const maxTracked = isPremium ? Infinity : 5;
  const canTrackMore = isPremium || trackedCount < maxTracked;

  // Helper function to extract values from MongoDB format
  const extractValue = (value) => {
    if (value && typeof value === 'object') {
      if ('$numberInt' in value) return parseInt(value.$numberInt, 10);
      if ('$numberDouble' in value) return parseFloat(value.$numberDouble);
      if ('$numberLong' in value) return parseInt(value.$numberLong, 10);
      if ('$date' in value) return new Date(parseInt(value.$date.$numberLong, 10));
      if ('$oid' in value) return value.$oid;
    }
    return value;
  };

  // Process offers to extract values from MongoDB format
  const processOffers = (offers) => {
    if (!offers || !Array.isArray(offers)) return [];
    
    return offers.map(offer => ({
      ...offer,
      plan_id: offer.plan_id ? extractValue(offer.plan_id) : null,
      promo_price: offer.promo_price ? extractValue(offer.promo_price) : null,
      discount_percent: offer.discount_percent ? extractValue(offer.discount_percent) : null
    }));
  };

  // Get promos for a specific merchant
  const getMerchantPromos = (merchantId) => {
    return activePromosData[merchantId] || [];
  };

  // Get all active promos count for a merchant
  const getActivePromosCount = (merchantId) => {
    const promos = getMerchantPromos(merchantId);
    return promos.length;
  };

  // Check if merchant has active promos
  const hasActivePromos = (merchantId) => {
    return getActivePromosCount(merchantId) > 0;
  };

  // Helper function to calculate countdown timer
  const getCountdownTimer = (endDate) => {
    const date = extractValue(endDate);
    const end = new Date(date);
    const diffTime = end - currentTime;
    
    if (diffTime <= 0) {
      return { text: t('trackedMerchants.promoExpired'), urgent: false };
    }

    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
    const diffSeconds = Math.floor((diffTime % (1000 * 60)) / 1000);

    if (diffDays > 7) {
      return { 
        text: `${t('trackedMerchants.validUntil')} ${formatDate(end)}`, 
        urgent: false 
      };
    }

    let timerText = '';
    if (language === 'it') {
      if (diffDays > 0) {
        timerText = `${diffDays}g ${diffHours}h ${diffMinutes}m`;
      } else if (diffHours > 0) {
        timerText = `${diffHours}h ${diffMinutes}m ${diffSeconds}s`;
      } else {
        timerText = `${diffMinutes}m ${diffSeconds}s`;
      }
    } else {
      if (diffDays > 0) {
        timerText = `${diffDays}d ${diffHours}h ${diffMinutes}m`;
      } else if (diffHours > 0) {
        timerText = `${diffHours}h ${diffMinutes}m ${diffSeconds}s`;
      } else {
        timerText = `${diffMinutes}m ${diffSeconds}s`;
      }
    }

    return { 
      text: timerText, 
      urgent: diffDays <= 3 
    };
  };

  // Get the most urgent promo for display
  const getMostUrgentPromo = (merchantId) => {
    const promos = getMerchantPromos(merchantId);
    if (promos.length === 0) return null;

    let mostUrgent = null;
    let minTime = Infinity;

    promos.forEach(promo => {
      const endDate = extractValue(promo.end_date);
      const end = new Date(endDate);
      const diffTime = end - currentTime;
      
      if (diffTime > 0 && diffTime < minTime) {
        minTime = diffTime;
        mostUrgent = promo;
      }
    });

    return mostUrgent;
  };

  // Filter and sort merchants - active promos first
  const filteredMerchants = trackedMerchants
    .filter(merchant => 
      merchant && (
        (merchant.name && merchant.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (merchant.category && merchant.category.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    )
    .sort((a, b) => {
      const aHasPromos = hasActivePromos(a.id);
      const bHasPromos = hasActivePromos(b.id);
      
      if (aHasPromos && !bHasPromos) return -1;
      if (!aHasPromos && bHasPromos) return 1;
      
      return (a.name || '').localeCompare(b.name || '');
    });

  const totalPages = Math.ceil(filteredMerchants.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentMerchants = filteredMerchants.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Navigate to merchant page (when logo is clicked)
  const handleViewMerchant = (merchantId) => {
    if (merchantId) {
      navigate(`/mobile-merchant/${merchantId}`);
    }
  };

  // Handle external link click (website / affiliate)
  const handleMerchantWebsiteClick = (merchant) => {
    if (!merchant) return;
    
    const url = merchant.affiliate_link || merchant.website;
    if (!url) {
      alert(t('trackedMerchants.noWebsite') || 'No website available');
      return;
    }
    
    if (window.confirm(t('trackedMerchants.redirectConfirm') || 'You are about to leave the app and visit the merchant website. Continue?')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleToggleNotification = (merchantId, currentStatus) => {
    if (!currentStatus) {
      performToggleNotification(merchantId, currentStatus);
    } else {
      const merchant = trackedMerchants.find(m => m.id === merchantId);
      setMerchantToToggle({
        id: merchantId,
        name: merchant?.name || t('trackedMerchants.thisMerchant')
      });
      setCurrentToggleStatus(currentStatus);
      setShowConfirmModal(true);
    }
  };

  const performToggleNotification = async (merchantId, currentStatus) => {
    setIsToggling(true);
    setCurrentTogglingId(merchantId);
    
    try {
      if (!httpClient) {
        alert(t('trackedMerchants.httpClientError'));
        return;
      }

      const response = await httpClient.request('/api/toggle-promo-notifications', {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          merchant_id: merchantId,
          enabled: !currentStatus
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          return performToggleNotification(merchantId, currentStatus);
        }
        const data = await response.json();
        if (data.limitReached) {
          alert(data.error || t('trackedMerchants.limitReached'));
          return;
        }
        throw new Error(data.error || t('trackedMerchants.updateFailed'));
      }

      const data = await response.json();
      
      if (data.success) {
        await fetchTrackedData();
        if (onToggleNotification) {
          onToggleNotification(merchantId, !currentStatus, data.trackedCount);
        }
      } else {
        alert(data.error || t('trackedMerchants.updateFailed'));
      }
    } catch (error) {
      console.error('Error toggling notification:', error);
      alert(t('trackedMerchants.updateFailed'));
    } finally {
      setIsToggling(false);
      setCurrentTogglingId(null);
      setShowConfirmModal(false);
      setMerchantToToggle(null);
      setCurrentToggleStatus(null);
    }
  };

  const handleConfirmToggle = () => {
    if (merchantToToggle && currentToggleStatus !== null) {
      performToggleNotification(merchantToToggle.id, currentToggleStatus);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('common.na');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'it' ? 'it-IT' : 'en-GB', { 
        day: 'numeric', 
        month: 'short'
      });
    } catch (error) {
      return t('common.na');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#253985]" />
        <span className="ml-2 text-gray-600">{t('trackedMerchants.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 bg-gradient-to-br from-[#253985]/10 to-[#1a2a6d]/10 rounded-xl border border-[#253985]/20">
        <AlertCircle className="h-12 w-12 text-[#253985] mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-800 mb-2">{t('trackedMerchants.errorLoadingData')}</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={fetchTrackedData}
          className="bg-[#253985] hover:bg-[#1a2a6d] text-white px-4 py-2 rounded-lg transition-colors"
        >
          {t('common.tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <div className="flex-1 overflow-auto pb-20">
        <div className="p-4 space-y-4">
          {/* Tracking Limit Info */}
          {!isPremium && (
            <div className="bg-[#253985]/10 p-3 rounded-lg border border-[#253985]/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-[#253985]/10 flex items-center justify-center">
                    <Crown className="h-4 w-4 text-[#253985]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#253985] font-medium">
                      {t('trackedMerchants.tracking')} {trackedCount}/{maxTracked}
                    </p>
                    <p className="text-xs text-[#253985]/80">
                      {canTrackMore 
                        ? `${maxTracked - trackedCount} ${t('trackedMerchants.slotsRemaining')}`
                        : t('trackedMerchants.limitReached')
                      }
                    </p>
                  </div>
                </div>
                {!canTrackMore && (
                  <button
                    onClick={() => navigate("/mobile-upgrade")}
                    className="flex items-center gap-1 bg-gradient-to-r from-[#253985] to-amber-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold"
                  >
                    <Crown className="h-3 w-3" />
                    {t('common.upgrade')}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Header with search */}
          <div className="flex flex-col gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('trackedMerchants.searchPlaceholder')}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            
            <div className="text-sm text-gray-600 text-center">
              {filteredMerchants.length} {t('trackedMerchants.merchants')}
            </div>
          </div>

          {/* Active Promos Section */}
          {filteredMerchants.some(m => hasActivePromos(m.id)) && (
            <div className="bg-gradient-to-r from-[#253985]/10 to-[#1a2a6d]/10 border border-[#253985]/20 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#253985]/10 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-[#253985]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#253985] text-sm">{t('trackedMerchants.activePromotions')}</h3>
                  <p className="text-xs text-[#253985]/80">
                    {filteredMerchants.filter(m => hasActivePromos(m.id)).length} {t('trackedMerchants.haveActivePromos')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tracked Merchants Cards */}
          <div className="space-y-2">
            {currentMerchants.length > 0 ? (
              currentMerchants.map((merchant) => {
                const hasActivePromos = getActivePromosCount(merchant.id) > 0;
                const mostUrgentPromo = getMostUrgentPromo(merchant.id);
                const loadingPromo = loadingPromos[merchant.id];
                const hasWebsite = !!(merchant.affiliate_link || merchant.website);
                
                return (
                  <div 
                    key={merchant.id} 
                    className={`p-3 rounded-lg border ${
                      hasActivePromos 
                        ? 'bg-[#253985]/5 border-[#253985] shadow-sm' 
                        : 'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Logo Section - now clickable, size increased */}
                      <div className="relative cursor-pointer" onClick={() => handleViewMerchant(merchant.id)}>
                        <div className={`w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center border ${
                          hasActivePromos 
                            ? 'border-[#253985]/30 bg-white' 
                            : 'border-gray-200 bg-white'
                        }`}>
                          {merchant.logo_url ? (
                            <img
                              src={merchant.logo_url}
                              alt={merchant.name}
                              className="w-10 h-10 object-contain"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI4IiBmaWxsPSJub25lIiBzdHJva2U9IiMyNTM5ODUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cGF0aCBkPSJNMyA5bDktNyA5IDd2MTFhMiAyIDAgMCAxLTIgMkg1YTIgMiAwIDAgMS0yLTJ6Ij48L3BhdGg+PHBvbHlsaW5lIHBvaW50cz0iOSAyMiA5IDEyIDE1IDEyIDE1IDIyIj48L3BvbHlsaW5lPjwvc3ZnPg==';
                              }}
                            />
                          ) : (
                            <div className="text-[#253985]">
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                <polyline points="9 22 9 12 15 12 15 22"></polyline>
                              </svg>
                            </div>
                          )}
                        </div>
                        {hasActivePromos && (
                          <div className="absolute -top-1.5 -left-1.5 w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow-sm flex items-center justify-center">
                            <Sparkles className="h-2 w-2 text-white" />
                          </div>
                        )}
                      </div>
                      
                      {/* Merchant Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className={`font-medium text-sm truncate mb-1 ${
                          hasActivePromos ? 'text-[#253985]' : 'text-gray-900'
                        }`}>
                          {merchant.name || t('trackedMerchants.unknownMerchant')}
                        </h4>
                        
                        {hasActivePromos && (
                          <div className="mb-1 flex items-center gap-1">
                            <span className="text-xs bg-[#253985]/10 text-[#253985] px-1.5 py-0.5 rounded-full flex items-center">
                              <Tag className="h-2.5 w-2.5 mr-0.5" />
                              {getActivePromosCount(merchant.id)} {t('trackedMerchants.activePromos')}
                            </span>
                          </div>
                        )}
                        
                        {/* Promo Information */}
                        {loadingPromo ? (
                          <div className="flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
                            <span className="text-xs text-gray-500">{t('trackedMerchants.checkingPromos')}</span>
                          </div>
                        ) : hasActivePromos && mostUrgentPromo ? (
                          <div>
                            <div className={`p-1.5 rounded ${
                              getCountdownTimer(mostUrgentPromo.end_date).urgent 
                                ? 'bg-red-50 border border-red-200' 
                                : 'bg-[#253985]/10 border border-[#253985]/20'
                            }`}>
                              <div className="flex items-center justify-center gap-1">
                                <Clock className={`h-3 w-3 ${
                                  getCountdownTimer(mostUrgentPromo.end_date).urgent 
                                    ? 'text-red-600' 
                                    : 'text-[#253985]'
                                }`} />
                                <span className={`text-xs font-medium ${
                                  getCountdownTimer(mostUrgentPromo.end_date).urgent 
                                    ? 'text-red-600' 
                                    : 'text-[#253985]'
                                }`}>
                                  {getCountdownTimer(mostUrgentPromo.end_date).text}
                                </span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            {(merchant.active_promos || 0) > 0 ? (
                              <div className="flex items-center gap-0.5">
                                <TrendingUp className="h-3 w-3 text-[#253985]" />
                                <span className="font-medium text-[#253985]">
                                  {merchant.active_promos} {t('trackedMerchants.activePromos')}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-500">
                                {t('trackedMerchants.noActivePromos')}
                              </span>
                            )}
                            
                            {merchant.last_promo_date && (
                              <div className="flex items-center gap-0.5">
                                <Calendar className="h-3 w-3 text-[#253985]" />
                                <span>
                                  {t('trackedMerchants.lastPromo')} {formatDate(merchant.last_promo_date)}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Centered Buttons */}
                      <div className="flex flex-col items-center justify-center gap-1 ml-2">
                        <button
                          onClick={() => handleToggleNotification(merchant.id, merchant.enabled)}
                          disabled={isToggling && currentTogglingId === merchant.id}
                          className={`p-2 rounded-lg flex items-center justify-center ${
                            merchant.enabled 
                              ? 'bg-red-100 text-red-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}
                          title={merchant.enabled 
                            ? t('trackedMerchants.notificationsOn') 
                            : t('trackedMerchants.notificationsOff')
                          }
                        >
                          {isToggling && currentTogglingId === merchant.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : merchant.enabled ? (
                            <Bell className="h-4 w-4" />
                          ) : (
                            <BellOff className="h-4 w-4" />
                          )}
                        </button>
                        
                        {/* External link button - hidden on iOS */}
                        {!isIOS && (
                          <button
                            onClick={() => handleMerchantWebsiteClick(merchant)}
                            disabled={!hasWebsite}
                            className={`p-2 rounded-lg flex items-center justify-center ${
                              hasWebsite 
                                ? 'bg-[#253985] text-white hover:bg-[#1a2a6d]' 
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                            title={hasWebsite 
                              ? t('trackedMerchants.visitWebsite') 
                              : t('trackedMerchants.noWebsite')
                            }
                          >
                            <ExternalLink className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <AlertCircle className="h-8 w-8 text-[#253985] mx-auto mb-2" />
                <p className="text-gray-600 text-sm">
                  {searchTerm 
                    ? t('trackedMerchants.noSearchResults') 
                    : t('trackedMerchants.noTrackedMerchants')
                  }
                </p>
                <p className="text-gray-500 text-xs mt-1 max-w-xs mx-auto">
                  {t('trackedMerchants.exploreMarketplace')}
                </p>
                <button
                  onClick={() => navigate("/mobile-marketplace")}
                  className="mt-3 px-4 py-2 bg-[#253985] text-white rounded-lg text-sm"
                >
                  {t('trackedMerchants.browseMerchants')}
                </button>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredMerchants.length > itemsPerPage && (
            <div className="flex items-center justify-center gap-3 mt-4">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 disabled:opacity-50"
                aria-label={t('common.previous')}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-sm">
                {t('common.page')} {currentPage} {t('common.of')} {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 disabled:opacity-50"
                aria-label={t('common.next')}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-4 w-full max-w-md">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold">
                {t('trackedMerchants.confirmRemoval')}
              </h3>
              <button onClick={() => setShowConfirmModal(false)}>
                <X className="h-6 w-6 text-gray-400" />
              </button>
            </div>
            <div className="mb-4">
              <p className="text-gray-600">
                {t('trackedMerchants.confirmRemoveMessage')} <span className="font-medium text-[#253985]">{merchantToToggle?.name}</span>?
              </p>
              <p className="text-gray-500 text-sm mt-1">
                {t('trackedMerchants.willNoLongerNotify')}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-2 border rounded-lg"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmToggle}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg flex items-center justify-center"
              >
                <BellOff className="h-4 w-4 mr-1" />
                {t('trackedMerchants.stopTracking')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default MobileTrackedMerchantPromoList;